<template>
  <div
    id="applet"
    class="applet"
  >
    <div class="applet__inner">
      <cdr-text
        class="applet__heading"
        tag="h2"
      >
        {{ heading }}
      </cdr-text>

      <cdr-banner
        v-if="hasHouseholdError"
        type="warning"
        data-ui="applet-household-warning"
      >
        <template #icon-left>
          <icon-warning-fill />
        </template>
        <cdr-text class="applet__household-warning">
          You are currently unable to {{ action }}.
        </cdr-text>
        <template #message-body>
          To {{ action }}, edit the name on your <cdr-link
            href="/account/settings"
            data-analytics-id="apply buy:householded_household member banner_online account"
          >
            online account
          </cdr-link> to match <cdr-link
            href="/membership/dashboard"
            data-analytics-id="apply buy:householded_household member banner_your membership"
          >
            your membership
          </cdr-link>
          <span>.</span>
        </template>
      </cdr-banner>

      <cdr-banner
        v-else-if="hasGeneralError"
        type="error"
        class="applet__error"
      >
        <template #icon-left>
          <icon-error-fill />
        </template>
        <cdr-text class="applet__error-text">
          <strong>Something went wrong.</strong>
          Please try again, or <cdr-link
            href="/help"
            class="cdr-link"
            @click="handleHelpClick"
          >
            contact us
          </cdr-link> for assistance.
        </cdr-text>
      </cdr-banner>

      <!-- Apply & Buy form here -->
      <slot />

      <div
        class="applet__cta"
        data-analytics-config="exclude"
      >
        <!-- /* c8 ignore start */ -->
        <cdr-button
          :disabled="!applicationEnabled"
          class="applet__submit"
          @click="$emit('submit', isPreApproval)"
        >
          <!-- /* c8 ignore end */ -->
          {{ ctaText }}
        </cdr-button>
        <cdr-button
          v-if="isPreApproval"
          data-ui="skip-and-apply-button"
          class="applet__cta__skip-and-apply"
          modifier="link"
          :disabled="!applicationEnabled"
          @click="$emit('submit')"
        >
          Skip pre-approval & apply now
        </cdr-button>
      </div>
      <p
        v-if="applicationEnabled"
        class="applet__instruction"
        data-ui="applet-instruction"
      >
        <span
          v-if="isPreApproval"
        >
          Thanks, <strong>{{ firstName }}</strong>.
          You're all set to see if<br>you're pre-approved or to apply now.
        </span>
        <span
          v-else
        >
          Thanks, {{ firstName }}! You're all set to apply.
        </span>
      </p>
      <p
        v-else
        class="applet__instruction"
        data-ui="applet-instruction"
      >
        Once you confirm your information above, you’ll be able to apply. Need help?
        <a
          href="https://www.rei.com/contact-us"
          target="_blank"
          data-analytics-id="applet_Contact us"
        >
          Contact us.
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import {
  CdrBanner,
  CdrButton,
  CdrLink,
  CdrText,
  IconErrorFill,
  IconWarningFill,
} from '@rei/cedar';
import { metricsLink } from '#utils/analytics';

export default {
  name: 'ApplyBuyAppletContainer',
  components: {
    CdrBanner,
    CdrButton,
    CdrLink,
    CdrText,
    IconErrorFill,
    IconWarningFill,
  },
  props: {
    applicationEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasGeneralError: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasHouseholdError: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPreApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
    firstName: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['submit'],
  computed: {
    heading() {
      return this.isPreApproval
        ? 'To see if you’re pre-approved or to apply, please complete the two steps below.'
        : 'To start your application, please complete the steps below.';
    },
    action() {
      return this.isPreApproval
        ? "see if you're pre-approved"
        : 'apply';
    },
    ctaText() {
      return this.isPreApproval
        ? 'See if you’re pre-approved'
        : 'Apply for the card now';
    },
  },
  methods: {
    /* c8 ignore start */
    handleHelpClick() {
      metricsLink('applet-error_Contact us');
    },
    /* c8 ignore end */
  },
};
</script>

<style lang="scss">

.applet {
  @include cdr-md-mq-up {
    padding: $cdr-space-four-x 0 0 $cdr-space-four-x;
  }
}

.applet__inner {
  display: grid;
  grid-row-gap: $cdr-space-one-x;
  padding-bottom: $cdr-space-one-x;
}

.applet__submit {
  display: block;
  width: 100%;
  text-align: center;
}

.applet__heading {
  @include cdr-text-heading-sans-500;
  max-width: 90%;
  margin: $cdr-space-one-x auto $cdr-space-quarter-x;
  text-align: center;
  @include cdr-md-mq-up {
    text-align: left;
    max-width: initial;
  }
}

.applet__household-warning {
  @include cdr-text-utility-sans-strong-200;
}

.applet__error {
  [class*=cdr-banner__message] {
    padding: $cdr-space-inset-quarter-x-stretch-top-bottom $cdr-space-inset-half-x;
  }
}

.applet__error-text {
  @include cdr-text-utility-sans-200;
}

.applet__instruction {
  max-width: 340px;
  text-align: center;
  margin: 0 auto;
  a {
    @include cdr-link-base-mixin;
  }
}

.applet__cta {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__skip-and-apply {
    @include cdr-text-utility-sans-strong-200;
    margin-top: $cdr-space-quarter-x;
    font-weight: 500;

    &,
    &:hover {
      color: $cdr-color-text-primary;

      &[disabled] {
        text-decoration: underline;
        color: $cdr-color-text-button-secondary-disabled;
      }
    }

  }
}
</style>
